import { formatDateShort } from '~/services/TimeService';

import { cellAddress, cellAt, findLabel, isoDateOf, textOf } from './sheetCells';
import type { ImportIssue, ImportSection, ParsedBlackout, SheetRows } from './types';

const SECTION_LABEL_COL = 1;
const FIRST_DATA_COL = 2;
// Keeps a sub-label search from running into the next section.
const SUB_LABEL_REACH = 12;
const HEADER_ROWS = 10;

export interface SheetContext {
  rows: SheetRows;
  sheet: string;
  issues: Array<ImportIssue>;
}

function report(
  context: SheetContext,
  section: ImportSection,
  severity: ImportIssue['severity'],
  row: number,
  col: number,
  message: string,
) {
  context.issues.push({ section, severity, cell: cellAddress(row, col), message });
}

function missingSection(context: SheetContext, section: ImportSection, problem: string) {
  context.issues.push({
    section,
    severity: 'error',
    message: `${problem} on the "${context.sheet}" sheet, so no ${section} will load.`,
  });
}

function findSubLabel(rows: SheetRows, label: string, sectionRow: number) {
  return findLabel(rows, label, { fromRow: sectionRow, toRow: sectionRow + SUB_LABEL_REACH, col: SECTION_LABEL_COL });
}

// Found by content, not a fixed stride: merged cells put entries two apart and a PCM can leave gaps.
function readDatedColumns(context: SheetContext, section: ImportSection, sectionRow: number) {
  const { rows } = context;
  const nameRow = findSubLabel(rows, 'Period Name', sectionRow);
  const fromRow = findSubLabel(rows, 'Date From', sectionRow);
  const toRow = findSubLabel(rows, 'Date To', sectionRow);
  if (!nameRow || !fromRow || !toRow) {
    missingSection(context, section, 'The Blackouts section has no Period Name, Date From and Date To rows');
    return [];
  }

  const width = Math.max(0, ...[nameRow, fromRow, toRow].map(({ row }) => rows[row]?.length ?? 0));
  const entries: Array<ParsedBlackout> = [];
  let unnamed = 0;
  for (let col = FIRST_DATA_COL; col < width; col++) {
    const name = textOf(cellAt(rows, nameRow.row, col));
    const fromCell = cellAt(rows, fromRow.row, col);
    const toCell = cellAt(rows, toRow.row, col);
    if (name == null && fromCell == null && toCell == null) {
      continue;
    }

    const startDate = isoDateOf(fromCell);
    const endDate = isoDateOf(toCell);
    const blackout = name
      ? `The "${name}" blackout`
      : `The blackout in column ${cellAddress(0, col).replace(/\d+/g, '')}`;
    const fixIt = 'Fix it in the workbook and upload again.';
    if (!startDate || !endDate) {
      report(
        context,
        section,
        'error',
        fromRow.row,
        col,
        `${blackout} won't load: it needs both a Date From and a Date To. ${fixIt}`,
      );
      continue;
    }
    const dates = `${formatDateShort(startDate)} to ${formatDateShort(endDate)}`;
    if (startDate > endDate) {
      report(
        context,
        section,
        'error',
        fromRow.row,
        col,
        `${blackout} won't load: it ends before it starts (${dates}). ${fixIt}`,
      );
      continue;
    }
    if (name == null) {
      unnamed += 1;
      report(
        context,
        section,
        'warning',
        nameRow.row,
        col,
        `The blackout from ${dates} has no Period Name, so it loads as "Blackout ${unnamed}".`,
      );
    }
    entries.push({ name, startDate, endDate });
  }
  return entries;
}

export function parseBlackouts(context: SheetContext): Array<ParsedBlackout> {
  const heading = findLabel(context.rows, 'Blackouts', { col: SECTION_LABEL_COL });
  if (!heading) {
    missingSection(context, 'blackouts', 'There is no Blackouts section');
    return [];
  }
  return readDatedColumns(context, 'blackouts', heading.row);
}

// The header states the currency costs are typed in, one cell right of its label.
export function parseCurrency({ rows }: SheetContext): string | null {
  const label = findLabel(rows, 'Currency', { toRow: HEADER_ROWS });
  return label ? (textOf(cellAt(rows, label.row, label.col + 1))?.toUpperCase() ?? null) : null;
}
